import { useEffect, useState } from 'react';
import { quoteDeskService } from '@/services/insurerQuoteDesk';

/** How often the count is refreshed (spec: every 60 seconds and on window focus). */
export const OVERDUE_REFRESH_MS = 60_000;

/**
 * #471: the red count of overdue Quote Desk requests beside the sidebar item.
 *
 * Reads `quote-desk/summary/`, refetching every 60 s and when the window
 * regains focus. Deliberately a plain effect (no react-query): the sidebar is
 * rendered in many places without a QueryClient, and a badge must never be able
 * to break the menu -- any failure simply shows nothing.
 */
export function QuoteDeskOverdueBadge({ pollMs = OVERDUE_REFRESH_MS }: { pollMs?: number }) {
  const [overdue, setOverdue] = useState(0);

  useEffect(() => {
    let active = true;
    const load = () => {
      quoteDeskService
        .summary()
        .then((summary) => {
          if (active) setOverdue(Number(summary?.overdue) || 0);
        })
        .catch(() => {
          if (active) setOverdue(0);
        });
    };
    load();
    const timer = window.setInterval(load, pollMs);
    window.addEventListener('focus', load);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', load);
    };
  }, [pollMs]);

  if (overdue <= 0) return null;
  return (
    <span
      data-testid="quote-desk-overdue-badge"
      aria-label={`${overdue} overdue`}
      className="ml-auto rounded-full bg-red-600 px-2 py-0.5 text-xs font-semibold text-white"
    >
      {overdue > 99 ? '99+' : overdue}
    </span>
  );
}

export default QuoteDeskOverdueBadge;
